import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Drawer,
  DrawerProps,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import React from 'react';
import { SectionErrorBoundary } from './section-error-boundary';

type ResponsiveWidth =
  | string
  | number
  | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string | number>>;

const DEFAULT_WIDTH: ResponsiveWidth = {
  xs: '100vw',
  md: '60vw',
  lg: '50vw',
  xl: '40vw',
};

export function SideDrawer(props: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  anchor?: DrawerProps['anchor'];
  width?: ResponsiveWidth;
  hideToolbar?: boolean;
  hideBackdrop?: boolean;
  title?: string;
}) {
  // Props
  const {
    open,
    onClose,
    children,
    anchor = 'right',
    width = DEFAULT_WIDTH,
    hideToolbar,
    hideBackdrop,
    title,
  } = props;

  return (
    <Drawer
      container={window.document.body}
      variant="temporary"
      anchor={anchor}
      open={open}
      onClose={props.disabled ? undefined : onClose}
      hideBackdrop={hideBackdrop}
      sx={{
        zIndex: 2147483601,
        // Allow interaction with content behind when no backdrop
        ...(hideBackdrop && { pointerEvents: 'none' }),
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width,
          height: '100dvh',
          // Re-enable pointer events on the paper itself
          pointerEvents: 'auto',
        },
      }}
    >
      {!hideToolbar && (
        <Toolbar
          sx={{
            justifyContent: 'flex-start',
            gap: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            disabled={props.disabled}
            onClick={onClose}
            aria-label="close drawer"
          >
            <CloseIcon />
          </IconButton>
          {title && (
            <Typography variant="h6" noWrap>
              {title}
            </Typography>
          )}
        </Toolbar>
      )}

      <Box
        sx={{
          height: '100dvh',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* A crash in the drawer shows the section error inside it */}
        <SectionErrorBoundary dense>{children}</SectionErrorBoundary>
      </Box>
    </Drawer>
  );
}
